/**
 * Approximate source line range for text copied or picked from the rendered preview.
 *
 * Used by BOTH the hover "add to chat" bubble and the preview copy handler, so the two produce the
 * same range for the same passage (a pasted chip and a bubble chip are interchangeable).
 *
 * The rendered text has no markdown markup and no list markers, and paragraphs are separated by
 * blank lines, so we compare after dropping markup characters and collapsing whitespace, keeping a
 * map from every compared character back to its source line. Start comes from the head of the
 * excerpt, end from its tail, which gives the true span of a multi-paragraph selection.
 */

const MARKUP_CHARS = /[*_`~#>|\\]/
const LINE_PREFIX = /^\s*(?:[-+*]|\d+[.)])\s+(?:\[[ xX]\]\s+)?/
const NEEDLE_LEN = 80

type Compact = { text: string; lineOf: number[] }

function compactWithLines(source: string): Compact {
  const lines = source.replace(/\r\n/g, '\n').split('\n')
  let text = ''
  const lineOf: number[] = []
  const push = (ch: string, line: number) => {
    if (ch === ' ' && (text.length === 0 || text.endsWith(' '))) return
    text += ch
    lineOf.push(line)
  }
  lines.forEach((raw, i) => {
    const line = raw.replace(LINE_PREFIX, '')
    for (const ch of line) {
      if (MARKUP_CHARS.test(ch)) continue
      push(/\s/.test(ch) ? ' ' : ch, i + 1)
    }
    push(' ', i + 1)
  })
  return { text, lineOf }
}

function compactExcerpt(excerpt: string): string {
  let out = ''
  for (const ch of excerpt) {
    if (MARKUP_CHARS.test(ch)) continue
    const c = /\s/.test(ch) ? ' ' : ch
    if (c === ' ' && (out.length === 0 || out.endsWith(' '))) continue
    out += c
  }
  return out.trim()
}

export type ExcerptLineRange = { lineStart: number; lineEnd: number }

function bodyForExcerptSearch(markdown: string): string {
  if (!markdown.trimStart().startsWith('---')) return markdown
  return stripFrontmatterForSearch(markdown.trimStart())
}

function dedupeRanges(hits: ExcerptLineRange[]): ExcerptLineRange[] {
  const out: ExcerptLineRange[] = []
  for (const hit of hits) {
    const prev = out[out.length - 1]
    if (prev && prev.lineStart === hit.lineStart && prev.lineEnd === hit.lineEnd) continue
    out.push(hit)
  }
  return out
}

/**
 * Every place the excerpt occurs. A repeated opening stays as separate hits.
 * The closing 80 characters must fall before the next copy of the opening,
 * so two chapters that share a first sentence do not collapse into one span.
 */
export function findLineRangesForExcerpt(markdown: string, excerpt: string): ExcerptLineRange[] {
  const body = bodyForExcerptSearch(markdown)
  const needle = compactExcerpt(excerpt)
  if (!needle) return []

  const { text, lineOf } = compactWithLines(body)
  const head = needle.slice(0, NEEDLE_LEN)
  const tail = needle.length > NEEDLE_LEN ? needle.slice(-NEEDLE_LEN) : ''
  const strict: ExcerptLineRange[] = []
  const heads: ExcerptLineRange[] = []
  let from = 0

  while (from <= text.length - head.length) {
    const startIdx = text.indexOf(head, from)
    if (startIdx === -1) break
    const lineStart = lineOf[startIdx]
    const headEnd = lineOf[startIdx + head.length - 1] ?? lineStart
    if (!lineStart) {
      from = startIdx + 1
      continue
    }
    const headRange = { lineStart, lineEnd: Math.max(lineStart, headEnd ?? lineStart) }
    if (!tail || tail === head) {
      strict.push(headRange)
    } else {
      const nextHead = text.indexOf(head, startIdx + head.length)
      const limit = nextHead === -1 ? text.length : nextHead
      const tailIdx = text.indexOf(tail, startIdx)
      if (tailIdx !== -1 && tailIdx < limit) {
        const lineEnd = lineOf[tailIdx + tail.length - 1] ?? lineStart
        strict.push({ lineStart, lineEnd: Math.max(lineStart, lineEnd) })
      } else {
        heads.push(headRange)
      }
    }
    from = startIdx + Math.max(1, head.length)
  }

  if (strict.length > 0) return dedupeRanges(strict)
  if (heads.length === 1 && tail) {
    const lineCount = Math.max(1, excerpt.trim().split('\n').filter((line) => line.trim()).length)
    const start = heads[0].lineStart
    return [{ lineStart: start, lineEnd: start + lineCount - 1 }]
  }
  return dedupeRanges(heads)
}

/** One hit wins. Several hits: the one closest to `preferLine` (the chip or block hint). */
export function pickNearestLineRange(
  hits: ExcerptLineRange[],
  preferLine?: number
): ExcerptLineRange | undefined {
  if (hits.length === 0) return undefined
  if (hits.length === 1 || preferLine == null || !Number.isFinite(preferLine)) return hits[0]
  return hits.reduce((best, hit) =>
    Math.abs(hit.lineStart - preferLine) < Math.abs(best.lineStart - preferLine) ? hit : best
  )
}

export function findLineRangeForExcerpt(
  markdown: string,
  excerpt: string
): { lineStart?: number; lineEnd?: number } {
  const hits = findLineRangesForExcerpt(markdown, excerpt)
  if (hits.length > 0) return hits[0]

  const body = bodyForExcerptSearch(markdown)
  const needle = compactExcerpt(excerpt)
  if (!needle) return {}

  const { text, lineOf } = compactWithLines(body)
  const head = needle.slice(0, NEEDLE_LEN)
  const startIdx = text.indexOf(head)

  if (startIdx === -1) {
    // Fallback: first non-empty line of the excerpt, single-line range
    const firstLine = excerpt.split('\n').map((l) => l.trim()).find(Boolean)
    if (!firstLine) return {}
    const lines = body.replace(/\r\n/g, '\n').split('\n')
    const lineIdx = lines.findIndex((l) => l.includes(firstLine.slice(0, 40)))
    if (lineIdx === -1) return {}
    return { lineStart: lineIdx + 1, lineEnd: lineIdx + 1 }
  }

  const lineStart = lineOf[startIdx]!
  let lineEnd = lineStart
  if (needle.length > NEEDLE_LEN) {
    const tail = needle.slice(-NEEDLE_LEN)
    const tailIdx = text.indexOf(tail, startIdx)
    if (tailIdx !== -1) {
      lineEnd = lineOf[tailIdx + tail.length - 1] ?? lineStart
    } else {
      // Tail not found (partly formatted); approximate by the excerpt's own line count
      lineEnd = lineStart + Math.max(1, excerpt.trim().split('\n').filter((l) => l.trim()).length) - 1
    }
  } else {
    const headEnd = startIdx + head.length - 1
    lineEnd = lineOf[headEnd] ?? lineStart
  }
  return { lineStart, lineEnd: Math.max(lineStart, lineEnd) }
}

function stripFrontmatterForSearch(content: string): string {
  if (!content.startsWith('---')) return content
  const end = content.indexOf('\n---', 3)
  if (end === -1) return content
  return content.slice(end + 4)
}
